import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings as SettingsIcon, DollarSign, Bell, Shield, User } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [cashoutAmount, setCashoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('wise');
  const [payoutEmail, setPayoutEmail] = useState('');
  const [cashoutHistory, setCashoutHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.creator_status) {
      fetchCashoutHistory();
    }
  }, [user]);

  const fetchCashoutHistory = async () => {
    try {
      const response = await axios.get(`${API}/creator/cashouts`, { withCredentials: true });
      setCashoutHistory(response.data);
    } catch (error) {
      console.error('Error fetching cashout history:', error);
    }
  };

  const handleCashout = async () => {
    const amount = parseInt(cashoutAmount);
    
    if (!amount || amount < 10) {
      toast.error('Minimum cashout: 10 credits (£10)');
      return;
    }

    if (!payoutEmail || !payoutEmail.includes('@')) {
      toast.error(`Valid ${payoutMethod === 'wise' ? 'Wise' : 'PayPal'} email required`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/creator/cashout`,
        {
          credits_amount: amount,
          payout_method: payoutMethod,
          payout_email: payoutEmail
        },
        { withCredentials: true }
      );
      
      toast.success(`💰 Cashout requested! £${response.data.payout_amount} will be sent via ${response.data.payout_method}`);
      setCashoutAmount('');
      setPayoutEmail('');
      fetchCashoutHistory();
      checkAuth();
    } catch (error) {
      console.error('Error requesting cashout:', error);
      toast.error(error.response?.data?.detail || 'Cashout failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl">
        <div className="flex items-center gap-3">
          <SettingsIcon className="h-8 w-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        </div>

        <Tabs defaultValue="account">
          <TabsList>
            <TabsTrigger value="account">
              <User className="h-4 w-4 mr-2" />
              Account
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            {user?.creator_status && (
              <TabsTrigger value="cashout">
                <DollarSign className="h-4 w-4 mr-2" />
                Cashout
              </TabsTrigger>
            )}
            <TabsTrigger value="privacy">
              <Shield className="h-4 w-4 mr-2" />
              Privacy
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <Card className="p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold mb-4 dark:text-white">Account Information</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Name</p>
                  <p className="font-medium dark:text-white">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                  <p className="font-medium dark:text-white">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <div className="flex gap-2 mt-1">
                    {user?.premium_status && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">Premium</span>
                    )}
                    {user?.creator_status && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Creator</span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold mb-4 dark:text-white">Browser Notifications</h3>
              
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Enable browser notifications to receive alerts for new messages, transactions, and updates even when Chronos is closed.
                </p>

                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Push Notifications</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {typeof Notification !== 'undefined' && Notification?.permission === 'granted' 
                          ? '✅ Enabled - You\'ll receive alerts' 
                          : '🔔 Click to enable notifications'}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        if (typeof Notification === 'undefined' || !('Notification' in window)) {
                          toast.error('Your browser does not support notifications');
                          return;
                        }

                        if (Notification.permission === 'default') {
                          toast.error('Your browser does not support notifications');
                          return;
                        }

                        if (Notification.permission === 'default') {
                          Notification.requestPermission().then(perm => {
                            if (perm === 'granted') {
                              toast.success('🔔 Notifications enabled!');
                              new Notification('Chronos Time Bank', {
                                body: '✅ You\'ll now receive notifications!',
                              });
                            } else {
                              toast.error('You blocked notifications. See instructions below.');
                            }
                          });
                        } else if (Notification.permission === 'granted') {
                          toast.success('✅ Notifications are already enabled!');
                        } else {
                          // Permission denied - show instructions
                          toast.error('Notifications blocked! Follow instructions below to enable.', { duration: 8000 });
                        }
                      }}
                      className={typeof Notification !== 'undefined' && Notification?.permission === 'granted' 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-teal-500 hover:bg-teal-600'}
                    >
                      {typeof Notification !== 'undefined' && Notification?.permission === 'granted' ? 'Enabled ✅' : 'Enable Notifications'}
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p className="font-medium mb-2">You'll be notified about:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• New messages received</li>
                    <li>• Transaction requests</li>
                    <li>• Transaction completed</li>
                    <li>• New reviews</li>
                    <li>• Referral bonuses</li>
                    <li>• Course enrollments (Creators)</li>
                  </ul>
                </div>

                {Notification?.permission === 'denied' && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                    <p className="font-bold text-red-700 dark:text-red-400 mb-2">🚫 Notifications Currently Blocked</p>
                    <p className="text-sm text-red-600 dark:text-red-300 mb-3">
                      You previously blocked notifications. To enable them:
                    </p>
                    <div className="text-sm text-red-700 dark:text-red-300 space-y-2">
                      <p><strong>Chrome/Edge:</strong></p>
                      <ol className="ml-4 space-y-1">
                        <li>1. Click the 🔒 lock icon in address bar</li>
                        <li>2. Click "Site settings"</li>
                        <li>3. Find "Notifications" → Change to "Allow"</li>
                        <li>4. Refresh this page</li>
                      </ol>
                      
                      <p className="mt-3"><strong>Firefox:</strong></p>
                      <ol className="ml-4 space-y-1">
                        <li>1. Click the 🛡️ shield icon in address bar</li>
                        <li>2. Click permissions → Notifications</li>
                        <li>3. Select "Allow"</li>
                        <li>4. Refresh this page</li>
                      </ol>

                      <p className="mt-3"><strong>Safari:</strong></p>
                      <ol className="ml-4 space-y-1">
                        <li>1. Safari menu → Settings → Websites</li>
                        <li>2. Notifications → Find chronostimebank.com</li>
                        <li>3. Change to "Allow"</li>
                        <li>4. Refresh this page</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Cashout Tab (Creator only) */}
          {user?.creator_status && (
            <TabsContent value="cashout" className="space-y-6">
              <Card className="p-6 dark:bg-gray-800">
                <h3 className="text-lg font-bold mb-4 dark:text-white">
                  💰 Convert Credits to Money
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Withdraw your earned credits as real money. 1 credit = £1
                </p>

                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 mb-6">
                  <p className="text-sm font-medium dark:text-white">
                    Your Balance: <span className="text-2xl text-teal-600 dark:text-teal-400">{user?.credits_balance}</span> credits
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    = £{user?.credits_balance} GBP available for cashout
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block dark:text-white">
                      Payout Method
                    </label>
                    <select
                      value={payoutMethod}
                      onChange={(e) => setPayoutMethod(e.target.value)}
                      className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="wise">💳 Wise Business (Recommended)</option>
                      <option value="paypal">💰 PayPal</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block dark:text-white">
                      Credits to Withdraw (min 10)
                    </label>
                    <Input
                      type="number"
                      min="10"
                      max={user?.credits_balance}
                      value={cashoutAmount}
                      onChange={(e) => setCashoutAmount(e.target.value)}
                      placeholder="10"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block dark:text-white">
                      {payoutMethod === 'wise' ? 'Wise Business Email' : 'PayPal Email'}
                    </label>
                    <Input
                      type="email"
                      value={payoutEmail}
                      onChange={(e) => setPayoutEmail(e.target.value)}
                      placeholder={payoutMethod === 'wise' ? "your@business.com" : "your.paypal@email.com"}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Funds sent to your {payoutMethod === 'wise' ? 'Wise Business' : 'PayPal'} account in 1-3 days
                    </p>
                  </div>

                  <Button
                    onClick={handleCashout}
                    disabled={loading}
                    className="w-full bg-green-500 hover:bg-green-600 text-white"
                  >
                    {loading ? 'Processing...' : `Withdraw £${cashoutAmount || 0}`}
                  </Button>
                </div>
              </Card>

              {/* Cashout History */}
              <Card className="p-6 dark:bg-gray-800">
                <h3 className="text-lg font-bold mb-4 dark:text-white">Cashout History</h3>
                <div className="space-y-3">
                  {cashoutHistory.length === 0 ? (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                      No cashouts yet
                    </p>
                  ) : (
                    cashoutHistory.map((cashout) => (
                      <div key={cashout.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <p className="font-medium dark:text-white">
                            {cashout.credits_amount} credits → £{cashout.payout_amount}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(cashout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          cashout.status === 'completed' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                            : cashout.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                        }`}>
                          {cashout.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </TabsContent>
          )}

          {/* Privacy Tab */}
          <TabsContent value="privacy">
            <Card className="p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold mb-4 dark:text-white">Privacy & Data</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Visit the <a href="/privacy" className="text-teal-600 hover:text-teal-700 font-medium">Privacy page</a> to:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600 dark:text-gray-300">
                <li>• Export your data (GDPR)</li>
                <li>• Delete your account</li>
                <li>• Manage privacy settings</li>
              </ul>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
